import { useMemo } from 'react';

import { expectedJiraWebhooksToCreateTasks } from './expectedJiraWebhooksToCreateTasks';
import { expectedPdWebhooksToCreateTasks } from './expectedPdWebhooksToCreateTasks';
import { expectedWebhooksSubscriptionsToCreateTasks } from './expectedWebhooksSubscriptionsToCreateTasks';
import { jiraWebhooksToDeleteTasks } from './jiraWebhooksToDeleteTasks';
import { pdWebhooksToDeleteTasks } from './pdWebhooksToDeleteTasks';
import { useAllWebhooksRawData } from './useAllWebhooksRawData';
import { webhooksSubscriptionsToDeleteTasks } from './webhooksSubscriptionsToDeleteTasks';

const getWebhooksTasks = ({ actionType, actionScope, expectedPd, pd, expectedJira, jira, expectedSubscriptions, subscriptions }) => {
  const createAll = actionType === 'recreate';
  const createMissing = actionType === 'fix';
  const deleteAll = actionType === 'recreate' || actionType === 'delete';
  const deleteUseless = actionType === 'fix';

  const findTasks = (expected, existing) => {
    let tasks = [];
    if (deleteAll) tasks = [...tasks, ...Object.values(existing).map(w => w.deleteTask)];
    if (deleteUseless) tasks = [...tasks, ...Object.values(existing).filter(w => !expected[w.key]).map(w => w.deleteTask)];
    if (createAll) tasks = [...tasks, ...Object.values(expected).map(w => w.createTask)];
    if (createMissing) tasks = [...tasks, ...Object.values(expected).filter(w => !existing[w.key]).map(w => w.createTask)];
    return tasks;
  };

  return [
    ...(actionScope === 'both' || actionScope === 'pd' ? [...findTasks(expectedPd, pd), ...findTasks(expectedSubscriptions, subscriptions)] : []),
    ...(actionScope === 'both' || actionScope === 'jira' ? findTasks(expectedJira, jira) : []),
  ];
};


const getWebhooksDataFromRawResponses = (data) => {
  const { data: currentAccountMapping } = data.accountsMapping;
  const { data: { accounts_mappings: otherConnectedAccounts } } = data.otherConnectedAccounts;
  const { data: { expected_webhooks: expectedWebhooks } } = data.expectedWebhooks;
  const { data: jiraWebhooks } = data.jiraWebhooks;
  const { webhooks: pdWebhooks } = data.pdWebhooks;
  const { data: subscriptions } = data.subscriptions;
  const otherConnectedAccountsIds = otherConnectedAccounts.map(a => a.id);

  const resolvedData = {
    pdWebhooks,
    jiraWebhooks,
    currentAccountMapping,
    otherConnectedAccountsIds,
    expectedWebhooks,
    subscriptions
  };

  return {
    pd: pdWebhooksToDeleteTasks(resolvedData),
    jira: jiraWebhooksToDeleteTasks(resolvedData),
    subscriptions: webhooksSubscriptionsToDeleteTasks(resolvedData),
    expectedPd: expectedPdWebhooksToCreateTasks(resolvedData),
    expectedJira: expectedJiraWebhooksToCreateTasks(resolvedData),
    expectedSubscriptions: expectedWebhooksSubscriptionsToCreateTasks(resolvedData)
  };
};

const useAllWebhooksValidationTasks = ({ actionType, actionScope }) => {
  const data = useAllWebhooksRawData();
  const { loading } = Object.values(data).find(x => x.loading) || {};
  const { error } = Object.values(data).find(x => x.error) || {};

  const processedWebhooks = useMemo(() => {
    if (loading || error) return null;
    return getWebhooksDataFromRawResponses(data);
  }, [data, loading, error]);

  const tasks = useMemo(() => {
    if (!processedWebhooks) return [];
    return getWebhooksTasks({ actionType, actionScope, ...processedWebhooks });
  }, [actionType, actionScope, processedWebhooks]);

  return useMemo(() => ({ loading, error, tasks }), [error, loading, tasks]);
};

export default useAllWebhooksValidationTasks;
